import { useState, useEffect } from 'react';

// Icon cache configuration
const CACHE_PREFIX = 'rush-player-icon-';
const CACHE_VERSION = '2'; // Incremented to clear corrupted data URI cache
const CACHE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Player number to icon filename mapping
const PLAYER_ICON_MAP: Record<number, string> = {
  1: 'player1-8bit.png',
  2: 'player2-8bit.png',
  3: 'player3-8bit.png',
  4: 'player4-8bit.png',
  5: 'player5-8bit.png',
  6: 'player6-8bit.png',
};

/**
 * Get cached player icon
 */
const getCachedIcon = (iconName: string): string | null => {
  try {
    const cacheKey = `${CACHE_PREFIX}${iconName}-v${CACHE_VERSION}`;
    const cached = localStorage.getItem(cacheKey);
    
    if (!cached) {
      return null;
    }
    
    const { dataUri, timestamp } = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache is expired
    if (now - timestamp > CACHE_EXPIRY_MS) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`🗑️ Player icon cache expired for ${iconName}, will refetch`);
      }
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`💾 Using cached player icon: ${iconName}`);
    }
    
    return dataUri;
  } catch (error) {
    console.error(`❌ Error reading player icon cache for ${iconName}:`, error);
    return null;
  }
};

/**
 * Cache player icon
 */
const cacheIcon = (iconName: string, dataUri: string): void => {
  try {
    const cacheKey = `${CACHE_PREFIX}${iconName}-v${CACHE_VERSION}`;
    const cacheData = {
      dataUri,
      timestamp: Date.now(),
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`💾 Cached player icon: ${iconName}`);
    }
  } catch (error) {
    console.warn(`⚠️ Failed to cache player icon ${iconName}:`, error);
  }
};

/**
 * Hook to load player icons with caching
 * Returns a map of player number -> data URI
 */
export function usePlayerIcons(): Record<number, string> {
  const [iconUrls, setIconUrls] = useState<Record<number, string>>({});

  useEffect(() => {
    const loadIcons = async () => {
      const playerNumbers = [1, 2, 3, 4, 5, 6];
      const loadedIcons: Record<number, string> = {};
      
      for (const playerNumber of playerNumbers) {
        const iconName = PLAYER_ICON_MAP[playerNumber];
        if (!iconName) continue;
        
        // Check cache first
        const cachedDataUri = getCachedIcon(iconName);
        
        if (cachedDataUri) {
          loadedIcons[playerNumber] = cachedDataUri;
          continue; // Skip API call, use cached version
        }
        
        // Cache miss or expired - fetch from get_icon API (S3 via Lambda)
        try {
          const { apiClient } = await import('@/services/api');
          const dataUrl = await apiClient.getIcon(iconName);
          
          loadedIcons[playerNumber] = dataUrl;
          
          // Cache the icon for future use
          cacheIcon(iconName, dataUrl);
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`✅ Fetched and cached player icon from API: ${iconName}`);
          }
        } catch (error) {
          console.error(`❌ Error loading player icon ${iconName}:`, error);
        }
      }
      
      setIconUrls(loadedIcons);
      
      if (process.env.NODE_ENV === 'development') {
        const cachedCount = playerNumbers.filter(n => getCachedIcon(PLAYER_ICON_MAP[n]) !== null).length;
        console.log(`🎨 Player Icons - Loaded ${Object.keys(loadedIcons).length} icons (${cachedCount} from cache)`);
      }
    };
    
    loadIcons();
  }, []);

  return iconUrls;
}

